<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-container">
      <div class="error-icon">⚠️</div>
      <h2>Something went wrong</h2>
      <p class="error-message">{{ userFriendlyMessage }}</p>

      <div v-if="showDetails" class="error-details">
        <h4>Technical Details:</h4>
        <pre>{{ errorDetails }}</pre>
      </div>

      <div class="error-actions">
        <button @click="retry" class="btn btn-primary">
          Try Again
        </button>
        <button @click="goHome" class="btn btn-secondary">
          Go to Dashboard
        </button>
        <button @click="toggleDetails" class="btn btn-link">
          {{ showDetails ? 'Hide' : 'Show' }} Details
        </button>
      </div>
    </div>
  </div>
  <slot v-else />
</template>

<script>
import { ref, onErrorCaptured, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { logError } from '@/services/errorBoundary';

export default {
  name: 'ErrorBoundary',
  props: {
    fallbackComponent: {
      type: Object,
      default: null
    },
    onError: {
      type: Function,
      default: null
    }
  },
  setup(props) {
    const router = useRouter();
    const hasError = ref(false);
    const errorDetails = ref('');
    const userFriendlyMessage = ref('');
    const showDetails = ref(false);
    const errorKey = ref(0);
    const errorTimeouts = new Set();

    const getErrorMessage = (error) => {
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        return 'Unable to connect to the server. Please check your internet connection.';
      }
      if (error.response?.status === 401) {
        return 'Your session has expired. Please refresh the page and try again.';
      }
      if (error.response?.status === 403) {
        return 'You do not have permission to perform this action.';
      }
      if (error.response?.status === 404) {
        return 'The requested resource was not found.';
      }
      if (error.response?.status >= 500) {
        return 'A server error occurred. Please try again later.';
      }
      return 'An unexpected error occurred. Please try again.';
    };

    const handleError = (error, info) => {
      hasError.value = true;
      userFriendlyMessage.value = getErrorMessage(error);
      errorDetails.value = `${error.message}\n\nStack: ${error.stack}\n\nComponent Info: ${info}`;

      logError(error, {
        component: 'ErrorBoundary',
        info,
        timestamp: new Date().toISOString()
      });

      if (props.onError) {
        props.onError(error, info);
      }
    };

    onErrorCaptured((error, instance, info) => {
      handleError(error, info);
      return false;
    });

    const retry = () => {
      // Clear any pending error timeouts
      errorTimeouts.forEach(timeout => clearTimeout(timeout));
      errorTimeouts.clear();

      hasError.value = false;
      errorDetails.value = '';
      userFriendlyMessage.value = '';
      showDetails.value = false;
      errorKey.value++;
    };

    const goHome = () => {
      // Clear any pending error timeouts
      errorTimeouts.forEach(timeout => clearTimeout(timeout));
      errorTimeouts.clear();

      hasError.value = false;
      router.push('/');
    };

    const toggleDetails = () => {
      showDetails.value = !showDetails.value;
    };

    // Auto-clear error after a timeout (optional)
    const setErrorTimeout = (duration = 60000) => {
      const timeout = setTimeout(() => {
        if (hasError.value) {
          retry();
        }
      }, duration);
      errorTimeouts.add(timeout);
    };

    // Cleanup on unmount
    onBeforeUnmount(() => {
      // Clear all pending timeouts
      errorTimeouts.forEach(timeout => clearTimeout(timeout));
      errorTimeouts.clear();

      // Reset error state
      hasError.value = false;
      errorDetails.value = '';
      userFriendlyMessage.value = '';
      showDetails.value = false;
    });

    return {
      hasError,
      errorDetails,
      userFriendlyMessage,
      showDetails,
      errorKey,
      retry,
      goHome,
      toggleDetails
    };
  }
};
</script>

<style scoped>
.error-boundary {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.error-container {
  max-width: 600px;
  text-align: center;
  background: var(--bg-card);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--status-inactive-bg);
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-container h2 {
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.error-message {
  color: var(--text-secondary);
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.5;
}

.error-details {
  background: var(--bg-secondary);
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 2rem;
  text-align: left;
}

.error-details h4 {
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.error-details pre {
  color: var(--text-secondary);
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--btn-primary);
  color: var(--text-inverse);
}

.btn-primary:hover {
  background: var(--btn-primary-hover);
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-secondary);
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

.btn-link {
  background: none;
  color: var(--btn-primary);
  border: none;
  text-decoration: underline;
  padding: 0.5rem;
}

.btn-link:hover {
  color: var(--btn-primary-hover);
}
</style>